import ApiService from "./api.service";
import {
  FALLBACK_FILENAME,
  parseFileNameFromDisposition,
  sanitizeFileName,
  triggerBlobDownload,
  downloadAxiosBlobResponse,
} from "@/utils/blobDownload";

const parseFileNameFromUrl = (fileUrl = "") => {
  const path = String(fileUrl).split("?")[0];
  const fileName = path.split("/").pop() || "";

  try {
    return decodeURIComponent(fileName) || FALLBACK_FILENAME;
  } catch {
    return fileName || FALLBACK_FILENAME;
  }
};

const parseBlobJsonMessage = async (response) => {
  if (
    !response ||
    ![400, 422].includes(response.status) ||
    !(response.data instanceof Blob)
  ) {
    return null;
  }

  const contentType = response.headers?.["content-type"] || "";
  if (!contentType.includes("application/json")) {
    return null;
  }

  try {
    const text = await response.data.text();
    const parsed = JSON.parse(text);
    return parsed?.message || parsed?.data?.message || null;
  } catch {
    return null;
  }
};

const FileDownloadService = {
  async downloadAzurePrivateFile(privateUrl) {
    if (!privateUrl) {
      return {
        success: false,
        message: "Missing file URL",
      };
    }

    try {
      const response = await ApiService.post(
        "azure/private-file/download",
        { url: privateUrl },
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob",
        },
      );

      downloadAxiosBlobResponse(response, parseFileNameFromUrl(privateUrl));

      return { success: true };
    } catch (error) {
      const message =
        (await parseBlobJsonMessage(error?.response)) ||
        error?.response?.data?.message ||
        null;
      return {
        success: false,
        message,
      };
    }
  },

  async downloadPublicFile(fileUrl, preferredFileName = "") {
    if (!fileUrl) {
      return {
        success: false,
        message: "Missing file URL",
      };
    }

    const fallbackName =
      sanitizeFileName(preferredFileName) || parseFileNameFromUrl(fileUrl);

    try {
      const response = await fetch(fileUrl, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status}`);
      }

      const contentType =
        response.headers.get("content-type") || "application/octet-stream";
      const dispositionFileName = parseFileNameFromDisposition(
        response.headers.get("content-disposition") || "",
      );
      const fileName =
        dispositionFileName !== FALLBACK_FILENAME
          ? dispositionFileName
          : fallbackName;

      const blob = await response.blob();
      triggerBlobDownload(new Blob([blob], { type: contentType }), fileName);

      return { success: true };
    } catch (error) {
      try {
        const anchor = document.createElement("a");
        anchor.href = fileUrl;
        anchor.download = fallbackName;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        return { success: true };
      } catch {
        return {
          success: false,
          message: error?.message || null,
        };
      }
    }
  },
};

export { FileDownloadService };
